import type { ComponentType, MouseEvent, ReactNode } from 'react';

import { Ellipsis } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ColorVariant } from '@/theme/tokens';
import { colorVariants } from '@/theme/tokens';

export type BreadcrumbItem = {
  label: string;
  icon?: ReactNode;
  href?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  [key: string]: unknown;
  variant?: ColorVariant;
};

export type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  separator?: ReactNode;
  className?: string;
  // oxlint-disable-next-line typescript/no-explicit-any
  linkComponent?: ComponentType<any>;
  variant?: ColorVariant;
  /** Number of items to always show at the start before collapsing. Default: 1 */
  itemsBeforeCollapse?: number;
  /** Number of items to always show at the end after collapsing. Default: 2 */
  itemsAfterCollapse?: number;
};

function BreadcrumbSeparator({
  separator,
}: Readonly<{ separator: ReactNode }>) {
  return (
    <span className="text-muted-foreground mx-2 select-none" aria-hidden="true">
      {separator}
    </span>
  );
}

function BreadcrumbItemRenderer({
  item,
  isLast,
  linkComponent,
  variant,
  separator,
}: Readonly<{
  item: BreadcrumbItem;
  isLast: boolean;
  // oxlint-disable-next-line typescript/no-explicit-any
  linkComponent: ComponentType<any> | string;
  variant: ColorVariant;
  separator: ReactNode;
}>) {
  // oxlint-disable-next-line typescript/no-explicit-any
  const LinkComponent = linkComponent as ComponentType<any>;
  const {
    label: _label,
    icon: _icon,
    onClick,
    href,
    variant: _variant,
    ...linkProps
  } = item;
  const content = item.icon ?? item.label;
  const hasLinkProps = href ?? onClick ?? Object.keys(linkProps).length > 0;
  const shouldRenderLink = hasLinkProps && !isLast;
  const itemVariant = item.variant ?? variant;
  const linkClasses = colorVariants.breadcrumb.link[itemVariant];
  const activeClasses = colorVariants.breadcrumb.active[itemVariant];

  return (
    <li className="flex items-center">
      {shouldRenderLink ? (
        <LinkComponent
          {...(href ? { href } : {})}
          {...linkProps}
          onClick={onClick}
          className={cn(linkClasses, !item.icon && 'truncate max-w-48')}
          aria-label={item.icon ? item.label : undefined}
        >
          {content}
        </LinkComponent>
      ) : (
        <span
          className={cn(
            isLast ? activeClasses : 'text-muted-foreground',
            !item.icon && 'truncate max-w-48'
          )}
        >
          {content}
        </span>
      )}
      {!isLast && <BreadcrumbSeparator separator={separator} />}
    </li>
  );
}

function Breadcrumbs({
  items,
  separator = '/',
  className,
  linkComponent,
  variant = 'neutral',
  itemsBeforeCollapse = 1,
  itemsAfterCollapse = 2,
}: Readonly<BreadcrumbsProps>) {
  const LinkComponent = linkComponent ?? 'a';
  const shouldCollapse =
    items.length > itemsBeforeCollapse + itemsAfterCollapse + 1;

  const visibleStart = shouldCollapse
    ? items.slice(0, itemsBeforeCollapse)
    : items;
  const collapsedItems = shouldCollapse
    ? items.slice(itemsBeforeCollapse, items.length - itemsAfterCollapse)
    : [];
  const visibleEnd = shouldCollapse
    ? items.slice(items.length - itemsAfterCollapse)
    : [];

  return (
    <nav
      aria-label="Breadcrumb"
      data-slot="breadcrumbs"
      className={cn(className)}
    >
      <ol className="flex items-center p-2 text-sm">
        {visibleStart.map((item, index) => (
          <BreadcrumbItemRenderer
            key={item.href ?? item.label}
            item={item}
            isLast={!shouldCollapse && index === items.length - 1}
            linkComponent={LinkComponent}
            variant={variant}
            separator={separator}
          />
        ))}
        {shouldCollapse && (
          <li className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="text-muted-foreground hover:text-foreground focus:ring-ring flex items-center rounded px-1 transition-colors focus:ring-2 focus:outline-none"
                aria-label="Show more breadcrumbs"
              >
                <Ellipsis className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {collapsedItems.map((item) => {
                  const {
                    label,
                    onClick: _onClick,
                    variant: _v,
                    ...linkProps
                  } = item;
                  const hasDestination =
                    linkProps.href ?? Object.keys(linkProps).length > 0;
                  return (
                    <DropdownMenuItem
                      key={item.href ?? label}
                      asChild={!!hasDestination}
                    >
                      {hasDestination ? (
                        <LinkComponent
                          {...linkProps}
                          className="w-full cursor-pointer"
                        >
                          {label}
                        </LinkComponent>
                      ) : (
                        <span>{label}</span>
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <BreadcrumbSeparator separator={separator} />
          </li>
        )}
        {visibleEnd.map((item, index) => (
          <BreadcrumbItemRenderer
            key={item.href ?? item.label}
            item={item}
            isLast={index === visibleEnd.length - 1}
            linkComponent={LinkComponent}
            variant={variant}
            separator={separator}
          />
        ))}
      </ol>
    </nav>
  );
}

export { Breadcrumbs };
export default Breadcrumbs;
